import { useCallback, useMemo } from "react"

import {
  useOrganizationUsersTransport,
  type OrganizationUser,
} from "../transport"

const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/i

/** Rendered in place of a creator when an asset records none. */
export const NO_CREATOR = "—"
const API_CREATOR_LABEL = "API"

interface CreatorResolver {
  resolveCreator: (creator: string | null | undefined) => string
  isLoading: boolean
}

/**
 * Resolves an asset's stored creator token to a human display name.
 *
 * Assets record their creator inconsistently: some store the User ULID (test
 * sets, agents), others the acting user's email or the literal "api" for
 * API-launched work (runs, metrics). This resolves any of those to the org
 * member's display name, falling back to the raw token so a creator outside the
 * current org's user list is still shown, and to a placeholder when absent.
 */
export function useCreatorResolver(): CreatorResolver {
  const { usersByUlid, isLoading } = useOrganizationUsersTransport()

  const usersByEmail = useMemo(() => {
    const byEmail: Record<string, OrganizationUser> = {}
    for (const user of Object.values(usersByUlid)) {
      if (user.email) {
        byEmail[user.email.toLowerCase()] = user
      }
    }
    return byEmail
  }, [usersByUlid])

  const resolveCreator = useCallback(
    (creator: string | null | undefined): string => {
      if (!creator) {
        return NO_CREATOR
      }
      const matched =
        usersByUlid[creator] ?? usersByEmail[creator.toLowerCase()]
      if (matched) {
        return matched.displayName || matched.email || creator
      }
      if (creator.toLowerCase() === "api") {
        return API_CREATOR_LABEL
      }
      if (ULID_PATTERN.test(creator)) {
        return isLoading ? "Loading..." : "Unknown"
      }
      return creator
    },
    [usersByUlid, usersByEmail, isLoading],
  )

  return { resolveCreator, isLoading }
}
